import {useLocation, Link} from 'react-router-dom'
import {MonoText} from '@/components/shared/MonoText'
import {useMemo} from 'react'
import {useI18n} from '@/i18n'
import {Icon} from '@/components/shared/Icon'
import {routes} from '@/lib/routes'
import {ArrowLeft} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card, CardContent} from '@/components/ui/card'
import {TextLink} from '@/components/shared/TextLink'
import {PageErrorCode} from '@/components/shared/PageErrorCode'
import {Stack} from '@/components/shared/Stack'
import {Strong} from '@/components/shared/Strong'
import {SmallText} from '@/components/shared/SmallText'

 
const ALL_ROUTES = [
    {path: '/', label: 'Dashboard'},
    {path: routes.admin.root(), label: 'Admin'},
    {path: routes.admin.roles.list(), label: 'Admin > Roles'},
    {path: routes.admin.roleAssignments.path(), label: 'Admin > Role Assignments'},
    {path: routes.connectors.root(), label: 'Connectors'},
    {path: routes.connectors.inbound.list(), label: 'Connectors > Inbound'},
    {path: routes.connectors.outbound.list(), label: 'Connectors > Outbound'},
    {path: routes.admin.notificationLog.path(), label: 'Admin > Notification Log'},
    {path: routes.admin.testData.path(), label: 'Admin > Test Data'},
    {path: routes.admin.errorLogs.list(), label: 'Admin > Error Log'},
    {path: routes.schema.root(), label: 'Schema'},
    {path: routes.schema.types.list(), label: 'Schema > Type Values'},
    {path: routes.schema.attributeDefinitions.path(), label: 'Schema > Attribute Definitions'},
    {path: routes.schema.formLayouts.list(), label: 'Schema > Form Layouts'},
    {path: routes.schema.relationshipSchema.path(), label: 'Schema > Relationship Schema'},
    {path: routes.schema.valueLists.list(), label: 'Schema > Value Lists'},
    {path: routes.identityManagement.root(), label: 'Identity Management'},
    {path: routes.identityManagement.parties.list(), label: 'Identity Management > Parties'},
    {path: routes.identityManagement.identities.list(), label: 'Identity Management > Identities'},
    {path: routes.systems.list(), label: 'Target Systems'},
    {path: routes.systems.resources.list(), label: 'Resources'},
    {path: routes.accessGovernance.root(), label: 'Access Governance'},
    {path: routes.accessGovernance.profiles.list(), label: 'Access Governance > Access Profiles'},
    {path: routes.accessGovernance.rules.list(), label: 'Access Governance > Restriction Rules'},
    {path: routes.backgroundJobs.root(), label: 'Background Jobs'},
    {path: routes.effectiveAccess.root(), label: 'Effective Access'},
    {path: routes.reporting.root(), label: 'Reporting'},
]
 

function levenshtein(a: string, b: string): number {
    const matrix: number[][] = Array.from({length: a.length + 1}, (_, i) =>
        Array.from({length: b.length + 1}, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
    )
    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            matrix[i][j] = a[i - 1] === b[j - 1]
                ? matrix[i - 1][j - 1]
                : 1 + Math.min(matrix[i - 1][j], matrix[i][j - 1], matrix[i - 1][j - 1])
        }
    }
    return matrix[a.length][b.length]
}

export function NotFound() {
    const i18n = useI18n()
    const location = useLocation()
    const currentPath = location.pathname

    const suggestions = useMemo(() => {
        return ALL_ROUTES
            .map((route) => ({
                ...route,
                distance: levenshtein(currentPath.toLowerCase(), route.path.toLowerCase()),
            }))
            .filter((r) => r.distance <= Math.max(currentPath.length * 0.6, 5))
            .sort((a, b) => a.distance - b.distance)
            .slice(0, 3)
    }, [currentPath])

    return (
        <Stack gap="lg" align="center" className="py-16">
            <PageErrorCode>404</PageErrorCode>
            <Strong size="base">{i18n.common.pageNotFound}</Strong>
            <SmallText>
                <MonoText className="bg-muted px-2">{currentPath}</MonoText>
            </SmallText>

            {suggestions.length > 0 && (
                <Card className="max-w-md">
                    <CardContent className="pt-6">
                        <Stack gap="sm">
                            <Strong weight="medium">{i18n.common.didYouMean}</Strong>
                            <Stack gap="xs">
                                {suggestions.map((s) => (
                                    <TextLink
                                        key={s.path}
                                        to={s.path}
                                        testId={`suggestion-${s.path.replace(/\//g, '-')}`}
                                    >
                                        {s.label}
                                    </TextLink>
                                ))}
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            <Button variant="outline" asChild data-testid="btn-back-to-dashboard">
                <Link to="/" data-testid="link-back-to-dashboard">
                    <Icon icon={ArrowLeft} className="mr-2"/>
                    {i18n.common.backToDashboard}
                </Link>
            </Button>
        </Stack>
    )
}
